/**
 * Which home screen a signed-in user gets.
 *
 * The checklist names four: Super Admin, Finance Manager, Sales Manager, Cashier. They are
 * not four sets of permissions -- every one of them holds `sales_analytics:read`, and the
 * server already decides whose rows come back. They are four different questions:
 *
 * * a **cashier** wants their till and their shelf, today;
 * * a **sales manager** wants the team against target and who is buying;
 * * a **finance manager** wants revenue against cost, and what the stock is worth;
 * * a **super admin** wants all of it, shallow, and a way into each.
 *
 * So this decides layout, never access. A dashboard that showed a panel the caller may not
 * read would get a 403 in that panel and nothing else; a dashboard that hides one shows a
 * person less than they are entitled to. Both are wrong, and neither is fixed by guessing
 * from the role -- which is why the panels below are chosen by what the reader is trying to
 * do, and the figures inside them are scoped by the API.
 *
 * A role with analytics access and no line here gets `general`: the two panels every role
 * can act on. Failing to a smaller dashboard rather than to the admin one matters, because
 * a role added to the matrix later must not inherit a screen designed for somebody else.
 */

export type DashboardKind = 'admin' | 'finance' | 'sales' | 'counter' | 'general'

/** Widest remit first: a user holding two roles gets the dashboard of the wider one. */
const BY_ROLE: Array<[string, DashboardKind]> = [
  ['super_admin', 'admin'],
  ['finance_manager', 'finance'],
  ['sales_manager', 'sales'],
  // Above the staff on the tills, because a manager holding both roles is reading the
  // shop rather than working a till -- but the same counter dashboard, since the shop is
  // what both of them are looking at.
  ['store_manager', 'counter'],
  ['store_staff', 'counter'],
]

export function dashboardFor(roleSlugs: string[]): DashboardKind {
  const held = new Set(roleSlugs)
  for (const [slug, kind] of BY_ROLE) {
    if (held.has(slug)) return kind
  }
  return 'general'
}

export interface DashboardLayout {
  title: string
  description: string
  /** The window this dashboard opens on. A cashier reads a day; finance reads a quarter. */
  defaultDays: number
  /** Till only. A cashier's home screen is the counter, not the order book. */
  posOnly: boolean
  panels: {
    trend: boolean
    channels: boolean
    targets: boolean
    products: boolean
    categories: boolean
    customers: boolean
    inventory: boolean
  }
}

const NONE: DashboardLayout['panels'] = {
  trend: false,
  channels: false,
  targets: false,
  products: false,
  categories: false,
  customers: false,
  inventory: false,
}

export const LAYOUTS: Record<DashboardKind, DashboardLayout> = {
  admin: {
    title: 'Company overview',
    description: 'Revenue, the team against target, and what the shelf is doing.',
    defaultDays: 30,
    posOnly: false,
    panels: {
      ...NONE,
      trend: true,
      channels: true,
      targets: true,
      products: true,
      inventory: true,
    },
  },
  finance: {
    title: 'Finance overview',
    description: 'Revenue against cost of sales, and what the stock is worth.',
    defaultDays: 90,
    posOnly: false,
    panels: { ...NONE, trend: true, channels: true, categories: true, inventory: true },
  },
  sales: {
    title: 'Sales overview',
    description: 'The team against target, what is selling, and who is buying.',
    defaultDays: 30,
    posOnly: false,
    panels: { ...NONE, trend: true, targets: true, products: true, customers: true },
  },
  counter: {
    title: 'Counter overview',
    description: 'What this till has taken, and what is about to run out behind it.',
    defaultDays: 7,
    posOnly: true,
    panels: { ...NONE, trend: true, products: true, inventory: true },
  },
  general: {
    title: 'Sales overview',
    description: 'Revenue and what is selling, for the accounts you can see.',
    defaultDays: 30,
    posOnly: false,
    panels: { ...NONE, trend: true, products: true },
  },
}
